import { useEffect, useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import { useDebounce } from "use-debounce";
import { FaRegFilePdf } from "react-icons/fa6";

import { getUploadUrl } from "src/Common/common";
import TagService from "src/Services/API/tag.service";
import Loader from "src/Components/Common/Loader";
import { getImageUrl } from "src/Common/common";

const isSameTag = (firstTag, secondTag) => {
  if (firstTag?.id && secondTag?.id) {
    return firstTag.id === secondTag.id;
  }

  return (firstTag?.name || "").trim().toLowerCase() === (secondTag?.name || "").trim().toLowerCase();
};

const TagInput = ({ selectedTags = [], onSelectedTagsChange }) => {
  const [tagText, setTagText] = useState("");
  const [debouncedTagText] = useDebounce(tagText, 500);
  const [tags, setTags] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  useEffect(() => {
    if (!debouncedTagText.trim()) {
      setTags([]);
      return;
    }

    let isCancelled = false;
    TagService.searchTags(debouncedTagText)
      .then((response) => {
        if (isCancelled) return;
        if (response?.status === 200 && Array.isArray(response?.data)) {
          setTags(response.data);
        } else {
          setTags([]);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setTags([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [debouncedTagText]);

  useEffect(() => {
    setIsDropdownVisible(tagText.trim().length > 0);
  }, [tagText, tags.length]);

  const trimmedTagText = tagText.trim();
  const hasExactTagMatch = tags.some(
    (tag) => (tag?.name || "").trim().toLowerCase() === trimmedTagText.toLowerCase(),
  );

  const setSelectedTags = (updater) => {
    const nextTags = typeof updater === "function" ? updater(selectedTags) : updater;
    onSelectedTagsChange?.(nextTags);
  };

  const addSelectedTag = (tag) => {
    setSelectedTags((prevTags) => {
      const alreadySelected = prevTags.some((selectedTag) => isSameTag(selectedTag, tag));
      if (alreadySelected) {
        return prevTags;
      }
      return [...prevTags, tag];
    });
  };

  const removeSelectedTag = (tag) => {
    setSelectedTags((prevTags) => prevTags.filter((currentTag) => !isSameTag(currentTag, tag)));
  };

  const handleCreateTag = async () => {
    if (!trimmedTagText) return;

    try {
      const response = await TagService.createTag({ name: trimmedTagText });
      if (response?.status === 201 || response?.status === 200) {
        const createdTag = response?.data;
        if (createdTag?.name) {
          addSelectedTag(createdTag);
          setTagText("");
          setIsDropdownVisible(false);
        }
      }
    } catch (error) {
      // Keep silent to match existing UX style.
    }
  };

  return (
    <div className="relative">
      <input
        placeholder="Add a tag"
        value={tagText}
        onChange={(event) => setTagText(event.target.value)}
        autoComplete="off"
        spellCheck={false}
        className="w-full h-[34px] border-[1px] border-borderColor rounded-[4px] px-3 text-[13px] font-OpenSansRegular text-titleColor"
      />
      {isDropdownVisible && (
        <div className="absolute left-0 right-0 mt-1 border-[1px] border-borderColor rounded-[5px] bg-white overflow-hidden z-20">
          <div className="max-h-[180px] overflow-auto">
            {tags.map((tag) => (
              <button
                type="button"
                key={tag.id || tag.name}
                onClick={() => {
                  addSelectedTag(tag);
                  setTagText("");
                  setIsDropdownVisible(false);
                }}
                className="w-full text-left px-2 py-2 border-b border-borderColor font-OpenSansRegular text-[13px] text-titleColor"
              >
                {tag.name}
              </button>
            ))}
            {!hasExactTagMatch && trimmedTagText && (
              <button
                type="button"
                onClick={handleCreateTag}
                className="w-full text-left px-2 py-2 font-OpenSansRegular text-[13px] font-[700] text-titleColor"
              >
                + add "{trimmedTagText}"
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {selectedTags.map((tag) => (
          <span
            key={tag.id || tag.name}
            className="h-[20px] px-2 bg-[#E6E7EC] text-[#404A6E] text-[11px] rounded-[999px] inline-flex items-center gap-1"
          >
            {tag?.name}
            <button type="button" className="text-[11px] leading-none" onClick={() => removeSelectedTag(tag)}>
              <img src={getImageUrl("/images/common/tag_close.png")} className="w-[15px]" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

const AttachmentEditModal = ({ shown, attachment, onCancel, onSave, isSaving = false }) => {
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!shown || !attachment) return;
    setDescription(attachment.description || "");
    setTags(
      Array.isArray(attachment.tags)
        ? attachment.tags
          .map((tag) => {
            if (!tag) return null;
            if (typeof tag === "string") {
              const name = tag.trim();
              return name ? { name } : null;
            }

            const name = (tag?.name || tag?.label || "").trim();
            if (!name) return null;

            return tag?.id ? { id: tag.id, name } : { name };
          })
          .filter(Boolean)
        : [],
    );
  }, [shown, attachment]);

  useEffect(() => {
    if (!shown) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shown, onCancel]);

  const imageSrc = useMemo(() => {
    if (!attachment?.fileName) return "";
    return getUploadUrl(attachment.fileName);
  }, [attachment]);
  const isPdf = (attachment?.fileName || "").toLowerCase().endsWith(".pdf");

  const handleSave = () => {
    onSave?.({
      description: description.trim(),
      tags: tags,
    });
  };

  if (!shown || !attachment) return null;

  return (
    <div className="fixed inset-0 z-[11000] bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        className="w-full max-w-[620px] bg-white rounded-[10px] border border-borderColor px-4 py-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="w-full flex items-center justify-between mb-3">
          <div className="w-[24px]" />
          <p className="text-center font-OpenSansRegular text-[14px] font-[700] text-titleColor">Edit Attachment</p>
          <button type="button" className="w-[24px] h-[24px] flex items-center justify-center" onClick={onCancel}>
            <IoClose size={20} className="text-subTitleColor" />
          </button>
        </div>

        <div className="w-full h-[260px] rounded-[6px] overflow-hidden border-[1px] border-borderColor bg-[#eceff4]">
          {isPdf ? (
            <div className="w-full h-full bg-[#f2f4f7] flex items-center justify-center text-subTitleColor">
              <FaRegFilePdf size={42} />
            </div>
          ) : (
            <img src={imageSrc} alt={attachment.displayName || "Attachment"} className="w-full h-full object-contain bg-black" />
          )}
        </div>

        <div className="mt-4">
          <p className="font-OpenSansRegular text-[14px] font-[700] text-titleColor mb-2">Tags</p>
          <TagInput selectedTags={tags} onSelectedTagsChange={setTags} />
        </div>

        <div className="mt-4">
          <p className="font-OpenSansRegular text-[14px] font-[700] text-titleColor mb-2">Description</p>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="w-full h-[34px] border-[1px] border-borderColor rounded-[4px] px-3 text-[13px] font-OpenSansRegular text-titleColor"
            placeholder="Write a short description"
          />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="h-[36px] border-[1px] border-borderColor rounded-[6px] font-OpenSansRegular text-[14px] font-[600] text-subTitleColor"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`h-[36px] flex items-center justify-center rounded-[6px] font-OpenSansSemiBold text-[14px] text-white ${isSaving ? "bg-blueColor/70 cursor-not-allowed" : "bg-blueColor"
              }`}
          >
            {isSaving ? <Loader variant="button" ariaLabel="saving-attachment" /> : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttachmentEditModal;
